import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

function makeCtaDef(label: string, url: string): SlotGroupDefinition {
  return {
    ...ctaDef,
    fields: {
      ...ctaDef.fields,
      url: { ...ctaDef.fields.url, defaultValue: url },
      label: { ...ctaDef.fields.label, defaultValue: label },
    },
  };
}

// ============================================================================
// Component
// ============================================================================

interface PageIntroProps {
  /** Per-instance path prefix (see shared-component pattern). Falls back to
      the canonical slot name so the component works standalone. */
  tokenPrefix?: string;
  /** Default heading richtext for this instance. */
  heading?: string;
  /** Default body richtext for this instance. */
  body?: string;
  /** Renders the CTA pill below the body. Defaults to true. */
  showCta?: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
}

/**
 * Interior-page intro with the same treatment as `VirtualTourIntro`: a large
 * uppercase heading, a centered body blurb, and an optional CTA pill. Each
 * `pages.json` instance stores its own copy via `tokenPrefix`.
 */
export function PageIntro({
  tokenPrefix,
  heading = '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
  body = '<p>Discover everything <span data-type="field-token" data-field="property_name"></span> has to offer.</p>',
  showCta = true,
  ctaLabel = 'Schedule a Tour',
  ctaUrl = '/schedule-a-tour',
}: PageIntroProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageintro';
  const headingPath = `${prefix}.heading`;
  const bodyPath = `${prefix}.body`;
  const ctaPath = `${prefix}.cta`;

  const runtimeCtaDef = useMemo(() => makeCtaDef(ctaLabel, ctaUrl), [ctaLabel, ctaUrl]);
  const cta = useSlotGroup(ctaPath, runtimeCtaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-6 md:pb-9"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path={headingPath}
          label="Heading"
          defaultValue={heading}
          as="h2"
          className="font-heading text-balance text-[clamp(1.75rem,6vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />

        <div className="max-w-3xl mx-auto md:px-10 lg:px-0">
          <RichTextContent
            path={bodyPath}
            label="Body Text"
            defaultValue={body}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        {showCta && (
          <div className="mt-6 flex justify-center">
            <EditableSlotGroup groupPath={ctaPath} definition={runtimeCtaDef}>
              <a
                href={cta.url || ctaUrl}
                target={cta._target || '_self'}
                className="pi-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {cta.label || ctaLabel}
              </a>
            </EditableSlotGroup>
          </div>
        )}
      </div>

      {/* Schema-lint anchors — hidden, never interactive. They carry the
          canonical literal paths so the static linter can match every schema
          key; the visible wrappers above use the per-instance dynamic path. */}
      <RichTextContent path="pageintro.heading" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="pageintro.body" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="pageintro.cta" definition={ctaDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .pi-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .pi-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PageIntro',
  displayName: 'Page Intro',
  tokens: {
    'pageintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'pageintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Discover everything <span data-type="field-token" data-field="property_name"></span> has to offer.</p>',
    },
  },
  slotGroups: {
    'pageintro.cta': ctaDef,
  },
};

(PageIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageIntro;
